import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicShell from "../layout/PublicShell";
import { cn } from "../lib/format";

type Probe = { name: string; ok: boolean | null; ms: number | null; note?: string };

const API_BASE = import.meta.env.VITE_API_BASE || "";

/**
 * Lightweight self-served status page. No third-party vendor — just
 * runs a few cheap checks against the public API on page load and
 * reports green/red. Good enough for a small audience; swap in
 * statuspage.io / instatus when traffic warrants it.
 */
export default function Status() {
  const [probes, setProbes] = useState<Probe[]>([
    { name: "API gateway", ok: null, ms: null },
    { name: "Frontend (this page)", ok: true, ms: 0, note: "If you're reading this, the SPA loaded." },
  ]);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      const t0 = performance.now();
      try {
        // /api/me without auth → expect 401 (means the API is up). Anything
        // else (network error, 5xx, no response) means a real outage.
        const r = await fetch(`${API_BASE}/api/me`, { method: "GET" });
        const ok = r.status === 401 || r.status === 200;
        if (!cancelled) {
          setProbes((p) => [
            { ...p[0], ok, ms: Math.round(performance.now() - t0),
              note: ok ? `responded ${r.status}` : `unexpected ${r.status}` },
            p[1],
          ]);
        }
      } catch (e) {
        if (!cancelled) {
          setProbes((p) => [
            { ...p[0], ok: false, ms: Math.round(performance.now() - t0),
              note: e instanceof Error ? e.message : "network error" },
            p[1],
          ]);
        }
      }
    }
    void run();
    return () => { cancelled = true; };
  }, []);

  const allOk = probes.every((p) => p.ok === true);
  const anyDown = probes.some((p) => p.ok === false);

  return (
    <PublicShell minimal>
      <div className="max-w-[680px] mx-auto px-6 py-16">
        <div className="font-mono text-[11px] tracking-[0.18em] uppercase text-ink-faint">
          status
        </div>
        <h1 className="mt-3 font-serif text-3xl md:text-4xl leading-tight">
          {anyDown ? (
            <>Something's <span className="italic text-terracotta">down.</span></>
          ) : allOk ? (
            <>Everything's <span className="italic" style={{ color: "#386641" }}>up.</span></>
          ) : (
            <>Checking…</>
          )}
        </h1>
        <p className="mt-2 font-serif italic text-ink-soft text-base">
          Live checks, run in your browser. No vendor in between.
        </p>

        <ul className="mt-10 divide-y divide-[rgba(42,36,26,0.10)] border-y border-[rgba(42,36,26,0.10)]">
          {probes.map((p) => (
            <li key={p.name} className="py-4 flex items-baseline gap-4">
              <span
                className={cn(
                  "w-2 h-2 rounded-full mt-1 shrink-0",
                  p.ok === true && "bg-[#386641]",
                  p.ok === false && "bg-terracotta",
                  p.ok === null && "bg-ink-faint/50",
                )}
              />
              <div className="flex-1 min-w-0">
                <div className="font-serif text-base">{p.name}</div>
                {p.note && (
                  <div className="font-mono text-[11px] text-ink-faint mt-1">{p.note}</div>
                )}
              </div>
              <div className="font-mono text-[11px] text-ink-faint shrink-0">
                {p.ms !== null ? `${p.ms}ms` : "checking…"}
              </div>
            </li>
          ))}
        </ul>

        <p className="mt-12 font-serif italic text-ink-faint text-[14px]">
          Issue not listed? Email{" "}
          <a href="mailto:hello@ilmli.ai" className="text-ink-soft underline decoration-ink-faint/40 hover:decoration-terracotta">
            hello@ilmli.ai
          </a>{" "}
          and I'll take a look.
        </p>

        <div className="mt-8 pt-6 border-t border-[rgba(42,36,26,0.12)] text-[13px] text-ink-faint">
          <Link to="/" className="underline decoration-ink-faint/40 hover:decoration-terracotta">
            Home
          </Link>
        </div>
      </div>
    </PublicShell>
  );
}
